import { useSuspenseQuery } from '@tanstack/react-query';
import qs from 'qs';
import { paths } from '@/generated/supplier-experience-api';
import apiClient from '@/lib/apiClient';
import { appendDateTimestamp } from '@/utils/appendDateTimestamp';
import { createTakerMarketUuidsMap } from '@/utils/takerMarketUuidMap';
import { TakerMarket } from './useTakerMarkets';

export type LockedInvoiceRequestBody = paths['/locked-invoices']['post']['requestBody']['content']['application/json'];
export type LockedInvoiceResponse =
  paths['/locked-invoices']['post']['responses']['200']['content']['application/json'];
export type LockedInvoiceFetchOptions = paths['/locked-invoices']['post']['parameters']['query'];
export type LockedInvoiceSearchFilters = LockedInvoiceRequestBody['search'];
export type LockedInvoice = LockedInvoiceResponse['data']['invoices'][0];

interface LockedInvoiceParams {
  makerOrganizationUuid: string;
  options?: LockedInvoiceFetchOptions;
  search?: LockedInvoiceSearchFilters;
  takerMarkets: TakerMarket[];
}

const fetchLockedInvoices = async ({
  makerOrganizationUuid,
  options,
  search,
  takerMarkets,
}: LockedInvoiceParams): Promise<LockedInvoiceResponse['data']> => {
  const limit = options?.limit ?? 100;
  const params = qs.stringify({ ...options, limit }, { addQueryPrefix: true });

  const response = await apiClient
    .post(`api/supplier-experience/locked-invoices${params}`, {
      json: {
        makerOrganizationUuid,
        supplierMarkets: takerMarkets.map((takerMarket) => ({
          marketUuid: takerMarket.marketUuid,
          supplierDivisionUuid: takerMarket.takerDivisionUuid,
        })),
        ...(search ? { search } : {}),
      } satisfies LockedInvoiceRequestBody,
    })
    .json<LockedInvoiceResponse>();

  return {
    ...response.data,
    invoices: response.data.invoices.map((invoice) => ({
      ...invoice,
      originalDepositDate: invoice.originalDepositDate ? appendDateTimestamp(invoice.originalDepositDate) : null,
    })),
  };
};

const useLockedInvoices = (params: LockedInvoiceParams) => {
  const { makerOrganizationUuid, options, search, takerMarkets } = params;
  const isValid = !!makerOrganizationUuid && !!takerMarkets.length;
  const takerMarketUuidsMap = createTakerMarketUuidsMap(takerMarkets).sort();

  return useSuspenseQuery({
    queryKey: isValid
      ? ['locked-invoices', makerOrganizationUuid, options, search, ...takerMarketUuidsMap]
      : ['locked-invoices-invalid'],
    queryFn: () => (isValid ? fetchLockedInvoices(params) : null),
  });
};

export default useLockedInvoices;
